(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('nf-slider')) {
    class NfSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.slides = Array.from(this.track ? this.track.children : []);
        this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
        if (this.slides.length < 2) return;

        this.index = 0;
        this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.sliderDot))));

        this.track.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => this.sync(), 80);
        });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(this.index + 1);
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
        });

        ['mouseenter', 'focusin'].forEach((type) => this.addEventListener(type, () => this.stop()));
        ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.start()));
        this.start();
      }

      disconnectedCallback() {
        this.stop();
      }

      go(target) {
        const count = this.slides.length;
        this.index = (target + count) % count;
        const behavior = reduceMotion.matches ? 'auto' : 'smooth';
        this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior });
        this.mark();
      }

      sync() {
        const width = this.track.clientWidth || 1;
        this.index = Math.round(this.track.scrollLeft / width);
        this.mark();
      }

      mark() {
        this.dots.forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      start() {
        if (reduceMotion.matches || window.Shopify?.designMode) return;
        this.stop();
        this.timer = window.setInterval(() => {
          if (!document.hidden) this.go(this.index + 1);
        }, 7000);
      }

      stop() {
        window.clearInterval(this.timer);
      }
    }
    customElements.define('nf-slider', NfSlider);
  }

  if (!customElements.get('nf-patches')) {
    class NfPatches extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i, true));
          tab.addEventListener('keydown', (event) => {
            const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
            if (event.key in keys) {
              event.preventDefault();
              this.select((i + keys[event.key] + this.tabs.length) % this.tabs.length, true, true);
            } else if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              this.select(event.key === 'Home' ? 0 : this.tabs.length - 1, true, true);
            }
          });
        });
        this.onHash = () => this.fromHash(true);
        window.addEventListener('hashchange', this.onHash);
        if (!this.fromHash(false)) this.select(0, false);

        document.addEventListener('shopify:block:select', (event) => {
          const index = this.tabs.indexOf(event.target.closest?.('[role="tab"]'));
          if (index >= 0) this.select(index, false);
        });
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.onHash);
      }

      fromHash(scroll) {
        const id = decodeURIComponent(window.location.hash.slice(1));
        const index = this.panels.findIndex((panel) => panel && panel.id === id);
        if (index < 0) return false;
        this.select(index, false);
        if (scroll) this.closest('section')?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        return true;
      }

      select(index, userAction, focus = false) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (this.panels[i]) this.panels[i].hidden = !active;
        });
        const tab = this.tabs[index];
        if (focus) tab.focus();
        const list = tab.parentElement;
        if (list.scrollWidth > list.clientWidth) list.scrollLeft = tab.offsetLeft - 16;
        else if (tab.offsetTop < list.scrollTop || tab.offsetTop + tab.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = tab.offsetTop - list.clientHeight / 3;
        }
        if (userAction && window.matchMedia('(max-width: 989px)').matches) {
          this.panels[index]?.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }
    }
    customElements.define('nf-patches', NfPatches);
  }

  if (!customElements.get('nf-ladder')) {
    const DAY = 86400000;
    const dateFormat = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
    const timeFormat = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

    const weekStartUtc = (now) => {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const offset = (d.getUTCDay() + 6) % 7;
      return new Date(d.getTime() - offset * DAY);
    };

    class NfLadder extends HTMLElement {
      connectedCallback() {
        this.endpoint = (this.dataset.endpoint || '').trim();
        this.refreshMs = Math.max(30, Number(this.dataset.refresh) || 60) * 1000;
        this.maxRows = Number(this.dataset.rows) || 10;
        this.els = {
          status: this.querySelector('[data-ladder-status]'),
          week: this.querySelector('[data-ladder-week]'),
          countdown: this.querySelector('[data-ladder-countdown]'),
          podium: Array.from(this.querySelectorAll('[data-ladder-podium] > li')),
          rows: this.querySelector('[data-ladder-rows]'),
          offline: this.querySelector('[data-ladder-offline]'),
          updated: this.querySelector('[data-ladder-updated]'),
        };

        this.tickCountdown();
        this.countdownTimer = window.setInterval(() => this.tickCountdown(), 1000);

        if (!/^https:\/\//i.test(this.endpoint)) {
          this.setLive(false);
          return;
        }
        this.load();
        this.pollTimer = window.setInterval(() => {
          if (!document.hidden) this.load();
        }, this.refreshMs);
        this.onVisible = () => {
          if (!document.hidden) this.load();
        };
        document.addEventListener('visibilitychange', this.onVisible);
      }

      disconnectedCallback() {
        window.clearInterval(this.countdownTimer);
        window.clearInterval(this.pollTimer);
        if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
        this.controller?.abort();
      }

      tickCountdown() {
        const now = new Date();
        const start = this.weekStart || weekStartUtc(now);
        const end = this.weekEnd || new Date(start.getTime() + 7 * DAY);
        if (this.els.week) {
          this.els.week.textContent = `${dateFormat.format(start)} – ${dateFormat.format(new Date(end.getTime() - 1))}`;
        }
        if (!this.els.countdown) return;
        let left = Math.max(0, end.getTime() - now.getTime());
        const days = Math.floor(left / DAY);
        left -= days * DAY;
        const hours = Math.floor(left / 3600000);
        left -= hours * 3600000;
        const minutes = Math.floor(left / 60000);
        const seconds = Math.floor((left - minutes * 60000) / 1000);
        const pad = (n) => String(n).padStart(2, '0');
        this.els.countdown.textContent = `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }

      async load() {
        this.controller?.abort();
        this.controller = new AbortController();
        const timeout = window.setTimeout(() => this.controller.abort(), 10000);
        try {
          const response = await fetch(this.endpoint, {
            cache: 'no-store',
            credentials: 'omit',
            headers: { Accept: 'application/json' },
            signal: this.controller.signal,
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          this.render(await response.json());
        } catch (error) {
          if (error.name !== 'AbortError' || !this.hasData) this.setLive(this.hasData === true);
        } finally {
          window.clearTimeout(timeout);
        }
      }

      render(data) {
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const start = Date.parse(data?.week_start);
        const end = Date.parse(data?.week_end);
        this.weekStart = Number.isNaN(start) ? null : new Date(start);
        this.weekEnd = Number.isNaN(end) ? null : new Date(end);
        this.tickCountdown();

        const sorted = entries
          .map((entry, i) => ({
            rank: Number(entry.rank) || i + 1,
            name: String(entry.name ?? '').slice(0, 40) || '—',
            score: entry.score ?? entry.dungeons ?? '',
            best: entry.best_clear == null ? NaN : Number(entry.best_clear),
          }))
          .sort((a, b) => a.rank - b.rank);

        this.els.podium.forEach((card, i) => {
          const entry = sorted[i];
          card.querySelector('[data-name]').textContent = entry ? entry.name : '—';
          card.querySelector('[data-score]').textContent = entry ? `${entry.score} ${this.dataset.metric || ''}`.trim() : '';
        });

        if (this.els.rows) {
          const fragment = document.createDocumentFragment();
          sorted.slice(0, this.maxRows).forEach((entry) => {
            const row = document.createElement('tr');
            [`#${entry.rank}`, entry.name, String(entry.score), Number.isFinite(entry.best) ? `${entry.best}s` : '—'].forEach((value) => {
              const cell = document.createElement('td');
              cell.textContent = value;
              row.appendChild(cell);
            });
            fragment.appendChild(row);
          });
          this.els.rows.replaceChildren(fragment);
        }

        const updated = Date.parse(data?.updated_at);
        if (this.els.updated) {
          this.els.updated.hidden = Number.isNaN(updated);
          if (!Number.isNaN(updated)) this.els.updated.textContent = `Last update ${timeFormat.format(new Date(updated))}.`;
        }

        this.hasData = true;
        this.setLive(true, sorted.length === 0);
      }

      setLive(live, empty = false) {
        this.toggleAttribute('data-live', live);
        if (this.els.status) this.els.status.textContent = live ? this.dataset.labelLive : this.dataset.labelOffline;
        if (!this.els.offline) return;
        this.offlineText ??= this.els.offline.textContent;
        this.els.offline.textContent = live ? this.dataset.labelEmpty || '' : this.offlineText;
        this.els.offline.hidden = live && !empty;
      }
    }
    customElements.define('nf-ladder', NfLadder);
  }

  const startClocks = () => {
    const clocks = document.querySelectorAll('.nf-clock');
    if (!clocks.length) return;
    const tick = () => {
      clocks.forEach((clock) => {
        const timeZone = clock.dataset.timezone || undefined;
        try {
          clock.textContent = new Intl.DateTimeFormat('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone,
          }).format(new Date());
        } catch (error) {
          clock.textContent = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date());
        }
      });
    };
    tick();
    window.setInterval(tick, 30000);
  };

  const refreshEvents = () => {
    if (window.Shopify?.designMode) return;
    const now = Date.now() / 1000;
    document.querySelectorAll('[data-nf-events]').forEach((list) => {
      let visible = 0;
      list.querySelectorAll('.nf-event').forEach((event) => {
        const end = Number(event.dataset.end);
        const ended = end > 0 && end < now;
        event.hidden = ended;
        if (!ended) visible += 1;
      });
      const empty = list.querySelector('[data-events-empty]');
      if (empty) empty.hidden = visible > 0;
    });
  };

  const init = () => {
    startClocks();
    refreshEvents();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
